import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axiosClient from '../services/api';

const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            isGlobalLoading: false,
            error: null,

            setGlobalLoading: (state) => set({ isGlobalLoading: state }),

            // Actions
            login: async (email, password) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await axiosClient.post('/auth/login', { email, password });
                    const { user, token, refreshToken } = response.data;

                    set({
                        user,
                        token,
                        refreshToken,
                        isAuthenticated: true,
                        isLoading: false
                    });

                    return { success: true, user };
                } catch (error) {
                    set({
                        error: error.response?.data?.error || 'Login failed',
                        isLoading: false
                    });
                    return { success: false, error: error.response?.data?.error || 'Login failed' };
                }
            },

            register: async (name, email, password, role) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await axiosClient.post('/auth/register', {
                        name,
                        email,
                        password,
                        role
                    });
                    const { user, token, refreshToken } = response.data;

                    set({
                        user,
                        token,
                        refreshToken,
                        isAuthenticated: true,
                        isLoading: false
                    });

                    return { success: true, user };
                } catch (error) {
                    set({
                        error: error.response?.data?.error || 'Registration failed',
                        isLoading: false
                    });
                    return { success: false, error: error.response?.data?.error || 'Registration failed' };
                }
            },

            sendOTP: async (email) => {
                set({ isLoading: true, error: null });
                try {
                    await axiosClient.post('/auth/send-otp', { email });
                    set({ isLoading: false });
                    return { success: true };
                } catch (error) {
                    set({
                        error: error.response?.data?.error || 'Failed to send OTP',
                        isLoading: false
                    });
                    return { success: false, error: error.response?.data?.error || 'Failed to send OTP' };
                }
            },

            verifyOTP: async (email, otp, name, password, role, institution, expertise) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await axiosClient.post('/auth/verify-otp', {
                        email,
                        otp,
                        name,
                        password,
                        role,
                        institution,
                        expertise
                    });
                    const { user, token, refreshToken } = response.data;

                    console.log(user)

                    set({
                        user,
                        token,
                        refreshToken,
                        isAuthenticated: true,
                        isLoading: false
                    });

                    return { success: true, user };
                } catch (error) {
                    set({
                        error: error.response?.data?.error || 'Invalid OTP',
                        isLoading: false
                    });
                    return { success: false, error: error.response?.data?.error || 'Invalid OTP' };
                }
            },

            logout: async () => {
                set({ isGlobalLoading: true });

                try {

                    await new Promise((resolve) => setTimeout(resolve, 600));

                    set({
                        user: null,
                        token: null,
                        refreshToken: null,
                        isAuthenticated: false,
                        isGlobalLoading: false,
                    });

                } catch (error) {
                    set({ isGlobalLoading: false });
                }
            },

            setTokens: (token, refreshToken) => {
                set({ token, refreshToken });
            },

            fetchProfile: async () => {
                set({ isLoading: true, error: null });
                try {
                    const response = await axiosClient.get('/users/profile');
                    set({
                        user: response.data.user,
                        isLoading: false
                    });
                    return response.data.user;
                } catch (error) {
                    set({
                        error: error.response?.data?.error || 'Failed to fetch profile',
                        isLoading: false
                    });
                    return null;
                }
            },

            updateProfile: async (profileData) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await axiosClient.put('/users/profile', profileData);
                    const user = response.data.user || response.data.updatedUser;

                    const { token, refreshToken, isAuthenticated } = get();

                    set({
                        user,
                        token,
                        refreshToken,
                        isAuthenticated,
                        isLoading: false
                    });

                    return { success: true, user };
                } catch (error) {
                    set({
                        error: error.response?.data?.error || 'Failed to update profile',
                        isLoading: false
                    });
                    return { success: false, error: error.response?.data?.error || 'Failed to update profile' };
                }
            },

            sendResetPasswordOTP: async (email) => {
                set({ isLoading: true, error: null });
                try {
                    await axiosClient.post('/auth/request-password-reset-otp', { email });
                    set({ isLoading: false });
                    return { success: true };
                } catch (error) {
                    set({
                        error: error.response?.data?.error || 'Failed to send OTP',
                        isLoading: false
                    });
                    return { success: false, error: error.response?.data?.error || 'Failed to send OTP' };
                }
            },

            resetPassword: async (email, otp, newPassword) => {
                set({ isLoading: true, error: null });
                try {
                    await axiosClient.post('/auth/reset-password', { email, otp, newPassword });
                    set({ isLoading: false });
                    return { success: true };
                } catch (error) {
                    set({
                        error: error.response?.data?.error || 'Failed to reset password',
                        isLoading: false
                    });
                    return { success: false, error: error.response?.data?.error || 'Failed to reset password' };
                }
            },

            changePassword: async (currentPassword, newPassword) => {
                set({ isLoading: true, error: null });
                try {
                    await axiosClient.put('/users/change-password', {
                        currentPassword,
                        newPassword
                    });
                    set({ isLoading: false });
                    return { success: true };
                } catch (error) {
                    set({
                        error: error.response?.data?.error || 'Failed to change password',
                        isLoading: false
                    });
                    return { success: false, error: error.response?.data?.error || 'Failed to change password' };
                }
            },

            clearError: () => set({ error: null }),
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                refreshToken: state.refreshToken,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
);

export default useAuthStore;